<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="fw-bold">
                {{ recipientLabel }}
            </div>
            <pc-button
                v-if="!editMode && allowCorrections"
                small
                class="edit-recipient-contact-info-btn"
                v-bind:disabled="!isEditable(recipientDefinition)"
                v-on:click="onStartEdit">
                {{ editText }}
            </pc-button>
        </div>
        <div v-if="!editMode">
            <manage-request-contact-info
                v-bind:recipient="recipient"
                v-bind:recipient-definition="recipientDefinition"
                v-bind:current-contact-info="currentContactInfo"
                v-bind:updated-contact-info="updatedContactInfo"
                v-bind:allow-corrections="allowCorrections" />
        </div>
        <div v-if="editMode">
            <edit-recipient-contact-info
                v-bind:initial-contact-info="initialContactInfoForEdit"
                v-on:cancel="onEndEdit"
                v-on:update-contact-info="onUpdateContactInfo" />
            <hr />
        </div>
    </div>
</template>
<script>
    import { ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";

    import { mapI18n } from "Util/i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NS } from "I18n/common.i18n";

    import manageRequestContactInfo from "Forms/Components/Elements/envelope/manageRequestContactInfo.vue";
    import editRecipientContactInfo from "./editRecipientContactInfo.vue";

    export default {
        components: {
            manageRequestContactInfo,
            editRecipientContactInfo
        },
        inject: {
            getDataFieldValueByIdFn: {
                from: "getDataFieldValueByIdFn",
                default: () => {
                    throw "Missing `getDataFieldValueByIdFn`";
                }
            },
            getUserInfoByLoginIdFn: {
                from: "getUserInfoByLoginIdFn",
                default: () => {
                    throw "Missing `getUserInfoByLoginIdFn`";
                }
            }
        },
        props: {
            recipient: { type: Object, required: true },
            recipientDefinition: { type: Object, required: true },
            allowCorrections: { type: Boolean, required: false, default: false }
        },
        emits: ["start-edit", "end-edit", "update-contact-info"],
        data() {
            return {
                editMode: false,
                // Contact info after being updated manually
                updatedContactInfo: null
            };
        },
        computed: {
            recipientLabel() {
                return this.recipientDefinition.Label;
            },
            // The contact info as it would be based on current values in project data. May not
            // match what's saved for the recipient if the project data has been updated after the
            // request was created.
            currentContactInfo() {
                if (this.recipientDefinition.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField) {
                    const loginId = this.getDataFieldValueByIdFn(this.recipientDefinition.UserFieldId);
                    if (!loginId) {
                        return {
                            email: null,
                            first: null,
                            last: null
                        };
                    }
                    const user = this.getUserInfoByLoginIdFn(loginId);
                    return {
                        email: "",
                        first: user.FirstName,
                        last: user.LastName
                    };
                } else if (this.recipientDefinition.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.Fixed) {
                    return {
                        email: this.recipientDefinition.EmailFixed,
                        first: this.recipientDefinition.FirstNameFixed,
                        last: this.recipientDefinition.LastNameFixed
                    };
                }
                return {
                    email: this.getDataFieldValueByIdFn(this.recipientDefinition.EmailFieldId)?.trim(),
                    first: this.getDataFieldValueByIdFn(this.recipientDefinition.FirstNameFieldId)?.trim(),
                    last: this.getDataFieldValueByIdFn(this.recipientDefinition.LastNameFieldId)?.trim()
                };
            },
            // Contact info saved in the existing recipient
            originalContactInfo() {
                const first = this.recipient.FirstName;
                const last = this.recipient.LastName;
                const email =
                    this.recipientDefinition.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField
                        ? ""
                        : this.recipient.Email;
                return {
                    first,
                    last,
                    email
                };
            },
            // Order of precedence for initial contact info when editing:
            // - already-manually updated contact info
            // - contact info as reflected in current, saved, non-draft project data
            // - contact info saved in recipient
            initialContactInfoForEdit() {
                return this.isContactInfoSpecified(this.updatedContactInfo)
                    ? this.updatedContactInfo
                    : this.isContactInfoSpecified(this.currentContactInfo)
                      ? this.currentContactInfo
                      : this.originalContactInfo;
            },
            ...mapI18n({ ...COMMON_KEYS }, COMMON_NS)
        },
        methods: {
            onStartEdit() {
                this.editMode = true;
                this.$emit("start-edit");
            },
            onEndEdit() {
                this.editMode = false;
                this.$emit("end-edit");
            },
            onUpdateContactInfo(updatedContactInfo) {
                this.updatedContactInfo = updatedContactInfo;
                this.$emit("update-contact-info", updatedContactInfo);

                this.onEndEdit();
            },
            isEditable(recipientDefinition) {
                if (recipientDefinition.ContactInfoType !== ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields) {
                    return false;
                }
                return true;
            },
            isContactInfoSpecified(contactInfoObj) {
                if (!contactInfoObj) {
                    return false;
                }
                return Boolean(contactInfoObj.first || contactInfoObj.last || contactInfoObj.email);
            }
        }
    };
</script>
<style lang="scss" scoped>
    .edit-recipient-contact-info-btn {
        min-width: 3rem !important;
    }
</style>
